import { HEX_PRIVATE_PREFIX } from "../languageUtils.js";
import {
  NoCodeCellDataframe,
  getDataframeName,
  getNoCodeCellInputReference,
} from "../sql/dataSourceTableConfig.js";
import { typedObjectEntries } from "../utils/typedObjects.js";

import {
  ChartCustomSort,
  ChartLayer,
  ChartSeries,
  ChartSortByValue,
  ChartSpec,
} from "./types.js";

/**
 * Returns the dataframe name for a given chart layer.
 */
export function getChartLayerDataframeName(layer: ChartLayer): string | null {
  return getDataframeName(layer.dataFrame ?? null);
}

/**
 * Returns a list of dataframe names referenced by a chart. If you just need to
 * get the raw dataframe object, @see getChartReferencedDataFrames
 */
export function getChartReferencedDataFrameNames(
  chart: ChartSpec,
): readonly string[] {
  if (chart.type !== "layered") {
    return [];
  }

  const dataFrameSet = new Set<string>();
  chart.layers.forEach((layer) => {
    const dataframeName = getChartLayerDataframeName(layer);
    if (dataframeName != null) {
      dataFrameSet.add(dataframeName);
    }

    layer.series.forEach((series) => {
      if (series.dataFrame != null) {
        dataFrameSet.add(series.dataFrame);
      }
    });
  });
  return Array.from(dataFrameSet);
}

/**
 * Returns a list of values referenced by a chart - is either the name of the
 * dataframe or the path to table (if WHNC)
 */
export function getChartReferencedInputs(chart: ChartSpec): readonly string[] {
  if (chart.type !== "layered") {
    return [];
  }

  const dataFrameSet = new Set<string>();
  chart.layers.forEach((layer) => {
    const inputName = layer.dataFrame
      ? getNoCodeCellInputReference(layer.dataFrame)
      : null;
    if (inputName != null) {
      dataFrameSet.add(inputName);
    }

    layer.series.forEach((series) => {
      if (series.dataFrame != null) {
        dataFrameSet.add(series.dataFrame);
      }
    });
  });
  return Array.from(dataFrameSet);
}

/**
 * Returns a list of dataframe objects (either string or a DataSourceTableConfig
 * object) referenced by a chart. If you just need to get the dataframe name,
 * @see getChartReferencedDataFrameNames
 */
export function getChartReferencedDataFrames(
  chart: ChartSpec,
): readonly NoCodeCellDataframe[] {
  if (chart.type !== "layered") {
    return [];
  }

  const dataFrameSet = new Set<NoCodeCellDataframe>();
  chart.layers.forEach((layer) => {
    if (layer.dataFrame != null) {
      dataFrameSet.add(layer.dataFrame);
    }
    layer.series.forEach((series) => {
      if (series.dataFrame != null) {
        dataFrameSet.add(series.dataFrame);
      }
    });
  });
  return Array.from(dataFrameSet);
}

export function replaceChartDataFrameName<T extends ChartSpec>(
  chart: T,
  originalDataFrameName: string,
  newName: string,
): T {
  if (chart.type !== "layered") {
    // not supported, so should just be a noop
    return chart;
  }

  return {
    ...chart,
    layers: chart.layers.map((layer) => {
      const series = layer.series.map((s) => {
        if (s.dataFrame === originalDataFrameName) {
          return {
            ...s,
            dataFrame: newName,
          };
        }
        return s;
      });

      if (
        layer.dataFrame != null &&
        getDataframeName(layer.dataFrame) === originalDataFrameName
      ) {
        return { ...layer, series, dataFrame: newName };
      }
      return { ...layer, series };
    }),
  };
}

export type DataFrameColumnsByDataFrame = Record<string, string[]>;
export type SortByColsByDataFrameColByDataFrame = Record<
  string,
  Record<string, string>
>;
export type DataFrameColsAndSortByColsByDataFrame = {
  seriesValuesDataframeColumns: DataFrameColumnsByDataFrame;
  sortByDataFrameColumns: SortByColsByDataFrameColByDataFrame;
};

/**
 * Gets the list of dataframe columns that are used to compute the
 * order for series values, such as the categorical order for x-axis
 * or order of grouped bars/lines/facets
 * and when the columns are used for color sorting get the cross-axis metric
 * to be used to pre-sort the values
 */
export function getChartSeriesValuesDataFrameColumns(
  chart: ChartSpec,
): DataFrameColsAndSortByColsByDataFrame {
  const ret: DataFrameColsAndSortByColsByDataFrame = {
    seriesValuesDataframeColumns: {},
    sortByDataFrameColumns: {},
  };
  if (chart.type !== "layered") {
    return ret;
  }

  const columnSortBysByDataFrame: SortByColsByDataFrameColByDataFrame = {};
  const columnsByDataFrame: Record<string, Set<string>> = {};
  let firstDf: string | undefined;
  let firstSeries: ChartSeries | undefined;
  for (const layer of chart.layers) {
    for (const series of layer.series) {
      const dfName = series.dataFrame ?? getChartLayerDataframeName(layer);

      if (dfName == null) {
        continue;
      }

      if (firstDf == null) {
        firstDf = dfName;
        firstSeries = series;
      }

      if (columnsByDataFrame[dfName] == null) {
        columnsByDataFrame[dfName] = new Set<string>();
        columnSortBysByDataFrame[dfName] = {};
      }

      if (
        (ChartCustomSort.guard(layer.xAxis.sort) ||
          layer.xAxis.type === "string" ||
          // Always include the xAxis column for bar charts as we use this
          // to set bar widths for integer columns
          series.type === "bar") &&
        layer.xAxis.dataFrameColumn != null
      ) {
        columnsByDataFrame[dfName]?.add(layer.xAxis.dataFrameColumn);
      }

      if (series.colorDataFrameColumn != null) {
        columnsByDataFrame[dfName]?.add(series.colorDataFrameColumn);
        const colorOrder = series.colorOrder;
        const sortBy = series.dataFrameColumns?.[0];
        // For color encodings sorted by value, we want to
        // pre-sort the values by the cross-axis metric to pass to the
        // Vega-Lite layer, so keep track of the metric to be used to sort
        if (
          ChartSortByValue.guard(colorOrder) &&
          sortBy != null &&
          columnSortBysByDataFrame[dfName] != null
        ) {
          columnSortBysByDataFrame[dfName][series.colorDataFrameColumn] =
            sortBy;
        }
      }
    }

    [chart.facet?.facetHorizontal, chart.facet?.facetVertical].forEach(
      (facetDir) => {
        if (
          (ChartCustomSort.guard(facetDir?.sort) ||
            ChartSortByValue.guard(facetDir?.sort)) &&
          facetDir?.dataFrameColumn != null &&
          firstDf != null
        ) {
          columnsByDataFrame[firstDf]?.add(facetDir.dataFrameColumn);
        }

        // For facets sorted by value, we want to
        // pre-sort the values by the cross-axis metric to pass to the
        // Vega-Lite layer, so keep track of the metric to be used to sort
        const sortBy = firstSeries?.dataFrameColumns?.[0];
        if (
          ChartSortByValue.guard(facetDir?.sort) &&
          sortBy != null &&
          firstDf != null &&
          facetDir?.dataFrameColumn != null
        ) {
          columnSortBysByDataFrame[firstDf]![facetDir.dataFrameColumn] = sortBy;
        }
      },
    );
  }

  Object.entries(columnsByDataFrame).forEach(([df, columns]) => {
    if (columns.size > 0) {
      ret.seriesValuesDataframeColumns[df] = Array.from(new Set(columns));
    }
  });
  ret.sortByDataFrameColumns = columnSortBysByDataFrame;
  return ret;
}

export function getChartDataFrameColumns(
  chart: ChartSpec,
): DataFrameColumnsByDataFrame {
  // should be kept in sync with chart_cell.py:columns_in_spec
  // per Suchan, maybe this function's result could be pushed to Python
  // and the Python version could be removed
  const result: DataFrameColumnsByDataFrame = {};
  if (chart.type !== "layered") {
    return result;
  }

  for (const layer of chart.layers) {
    for (const series of layer.series) {
      const df = series.dataFrame ?? getChartLayerDataframeName(layer);
      if (df == null) {
        continue;
      }

      const columns: string[] = result[df] ?? [];

      // implicitly this is expecting every layer's df to have the facet cols
      if (chart.facet?.facetHorizontal?.dataFrameColumn != null) {
        columns.push(chart.facet.facetHorizontal.dataFrameColumn);
      }
      if (chart.facet?.facetVertical?.dataFrameColumn != null) {
        columns.push(chart.facet.facetVertical.dataFrameColumn);
      }

      if (layer.xAxis.dataFrameColumn != null) {
        columns.push(layer.xAxis.dataFrameColumn);
      }

      columns.push(...series.dataFrameColumns);
      if (series.colorDataFrameColumn != null) {
        columns.push(series.colorDataFrameColumn);
      }
      if (
        series.color?.type === "dataframe" &&
        series.color?.dataFrameColumn != null
      ) {
        columns.push(series.color?.dataFrameColumn);
      }
      if (
        series.opacity?.type === "dataframe" &&
        series.opacity?.dataFrameColumn != null
      ) {
        columns.push(series.opacity?.dataFrameColumn);
      }
      if (series.tooltip?.type === "manual") {
        for (const tooltip of series.tooltip.fields) {
          columns.push(tooltip.dataFrameColumn);
        }
      }

      result[df] = columns;
    }
  }

  typedObjectEntries(result).forEach(([df, columns]) => {
    result[df] = Array.from(new Set(columns));
  });
  return result;
}

export function getChartResultVariable({
  outputResult,
  resultVariable,
}: {
  resultVariable: string;
  outputResult: boolean;
}): string {
  return outputResult
    ? resultVariable
    : `${HEX_PRIVATE_PREFIX}chart_${resultVariable}`;
}
